<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

const route = useRoute();
const store = useStore();
const iframeRef = ref(null);

// Get current user and account data
const currentUser = useMapGetter('getCurrentUser');
const { accountId, currentAccount } = useAccount();

// Get current role from account user
const currentRole = computed(() => {
  return store.getters['getCurrentAccountUser']?.role || 'agent';
});

// Get teams the current user belongs to
const myTeams = useMapGetter('teams/getMyTeams');

// Track theme state
const isDarkTheme = ref(document.body.classList.contains('dark'));

// Dashboard app context payload (matches docs/implementacao_postmessage.md format)
const dashboardAppContext = computed(() => {
  const user = currentUser.value || {};

  return {
    account_id: accountId.value,
    account_name: currentAccount.value?.name || '',
    installation_url: window.location.origin,
    locale: currentAccount.value?.locale || 'pt_BR',
    theme: isDarkTheme.value ? 'dark' : 'light',
    currentAgent: {
      id: user.id,
      name: user.name,
      email: user.email,
      avatar_url: user.avatar_url,
      available_name: user.available_name,
      access_token: user.access_token,
      availability: user.availability_status || 'offline',
    },
    role: currentRole.value,
    userType: user.type,
    teams: (myTeams.value || []).map(t => ({ id: t.id, name: t.name })),
  };
});

const dashboardApp = computed(() => {
  const appId = route.params.id;
  return store.getters['dashboardApps/getRecords'].find(
    app => app.id === Number(appId)
  );
});

const isTypebot = computed(() => {
  return dashboardApp.value?.content?.some(
    item => item.model_app === 'typebot'
  );
});

const appUrl = computed(() => {
  if (!dashboardApp.value?.content?.[0]?.url) return '';
  return dashboardApp.value.content[0].url;
});

// ===========================
// v6.4.69: Iframe Route Sync
// Syncs iframe internal route with platform URL hash fragment.
// This enables: (1) route persistence on refresh, (2) shareable deep links
// ===========================

/**
 * Returns the expected origin of the iframe app URL.
 * Used to validate postMessage sources — only accept messages from our own app.
 */
const getAppOrigin = () => {
  if (!appUrl.value) return '';
  try {
    return new URL(appUrl.value).origin;
  } catch {
    return '';
  }
};

/**
 * Validates that a path is safe to store in the URL hash.
 * Blocks javascript:, data:, vbscript: and other dangerous URI schemes.
 * Only allows paths that start with '/' (relative navigation paths).
 */
const isSafePath = path => {
  if (!path || typeof path !== 'string') return false;
  const trimmed = path.trim();
  // Block dangerous URI schemes
  if (/^(javascript|data|vbscript|blob):/i.test(trimmed)) return false;
  // Must start with / (relative path only)
  if (!trimmed.startsWith('/')) return false;
  // Block path traversal attempts
  if (trimmed.includes('/../') || trimmed.endsWith('/..')) return false;
  return true;
};

/**
 * Extracts the iframe sub-path from current window hash.
 * Hash format: #/some/path?query=value
 * Returns the path portion (e.g., '/some/path?query=value') or empty string.
 */
const getHashPath = () => {
  const hash = window.location.hash;
  if (!hash || hash === '#' || hash === '#/') return '';
  // Remove leading '#' — keep the rest as-is (including leading /)
  const path = hash.substring(1);
  // Validate before returning
  return isSafePath(path) ? path : '';
};

/**
 * Builds the full iframe src URL by combining the base app URL with the
 * persisted hash path from the platform URL.
 *
 * Example:
 *   baseUrl: 'https://my-app.com'
 *   hash:    '#/settings/advanced'
 *   result:  'https://my-app.com/settings/advanced'
 *
 *   baseUrl: 'https://my-app.com/app'
 *   hash:    '#/settings'
 *   result:  'https://my-app.com/app/settings'
 */
const buildIframeSrc = () => {
  if (!appUrl.value) return '';
  const hashPath = getHashPath();
  if (!hashPath) return appUrl.value;

  // Remove trailing slash from base URL to avoid double slashes
  const base = appUrl.value.replace(/\/$/, '');
  // hashPath already starts with '/' (validated by isSafePath)
  return `${base}${hashPath}`;
};

// The actual iframe src — computed once on mount with hash, then static
const iframeSrc = ref('');

/**
 * Updates the platform URL hash fragment without triggering navigation.
 * Uses replaceState to avoid polluting browser history.
 * @param {string} path - The iframe internal path (e.g., '/settings/advanced?tab=2')
 */
const updateHash = path => {
  if (!path) return;
  // Normalize: ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  // Security: validate the path before writing to URL
  if (!isSafePath(normalizedPath)) return;
  const newHash = `#${normalizedPath}`;

  // Only update if actually changed
  if (window.location.hash !== newHash) {
    window.history.replaceState(null, '', newHash);
  }
};

/**
 * Extracts the relative path from an iframe URL by removing the base app URL portion.
 * This gives us just the internal route of the iframe app.
 *
 * Example:
 *   fullUrl: 'https://my-app.com/app/settings/advanced?tab=2'
 *   baseUrl: 'https://my-app.com/app'
 *   result:  '/settings/advanced?tab=2'
 */
const extractRelativePath = fullUrl => {
  if (!fullUrl || !appUrl.value) return '';

  try {
    const full = new URL(fullUrl);
    const base = new URL(appUrl.value);

    // Must be same origin
    if (full.origin !== base.origin) return '';

    // Get the base path (without trailing slash)
    const basePath = base.pathname.replace(/\/$/, '');

    // Get relative path by removing base path prefix
    let relativePath = full.pathname;
    if (basePath && relativePath.startsWith(basePath)) {
      relativePath = relativePath.substring(basePath.length);
    }

    // Add search params if present
    if (full.search) {
      relativePath += full.search;
    }

    // Ensure starts with /
    if (!relativePath.startsWith('/')) {
      relativePath = `/${relativePath}`;
    }

    // Don't store just '/' — that's the home, no need for hash
    if (relativePath === '/') return '';

    return relativePath;
  } catch {
    return '';
  }
};

// Send context to iframe via postMessage
const sendContextToIframe = () => {
  if (!iframeRef.value?.contentWindow) return;

  const eventData = {
    event: 'appContext',
    data: dashboardAppContext.value,
  };

  iframeRef.value.contentWindow.postMessage(JSON.stringify(eventData), '*');
};

// Handle messages from iframe
const handleMessage = event => {
  // Respond to fetch-info requests from iframe (legacy format — no origin check needed)
  if (event.data === 'chatwoot-dashboard-app:fetch-info') {
    sendContextToIframe();
    return;
  }

  // v6.4.69: Handle route change notifications from iframe
  // Security: validate the message origin matches our expected app origin
  const expectedOrigin = getAppOrigin();
  if (expectedOrigin && event.origin !== expectedOrigin) return;

  try {
    const data =
      typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

    if (data?.event !== 'routeChange') return;

    // Prefer 'path' (relative) over 'url' (absolute) — only process one
    if (data.path) {
      updateHash(data.path);
    } else if (data.url) {
      const relativePath = extractRelativePath(data.url);
      if (relativePath) {
        updateHash(relativePath);
      }
    }
  } catch {
    // Not a JSON message or not our format — ignore silently
  }
};

// Watch for theme changes using MutationObserver
let themeObserver = null;

const setupThemeObserver = () => {
  themeObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.attributeName === 'class') {
        const newIsDark = document.body.classList.contains('dark');
        if (newIsDark !== isDarkTheme.value) {
          isDarkTheme.value = newIsDark;
          // Push theme update to iframe
          sendContextToIframe();
        }
      }
    });
  });

  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
};

const fillTypebotEmail = () => {
  if (!isTypebot.value || !iframeRef.value) return;

  setTimeout(() => {
    try {
      const iframe = iframeRef.value;
      const email = store.getters['auth/currentUser']?.email || '';

      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        const emailInput = doc.querySelector('input[name="email"]');
        if (emailInput) {
          emailInput.value = email;
          emailInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } catch (error) {
        iframe.contentWindow.postMessage(
          {
            action: 'fill_email',
            email: email,
          },
          '*'
        );
      }
    } catch (e) {
      console.error('Erro ao preencher email no Typebot:', e);
    }
  }, 1500);
};

onMounted(() => {
  if (!store.getters['dashboardApps/getRecords'].length) {
    store.dispatch('dashboardApps/get');
  }

  // Add message listener for iframe requests
  window.addEventListener('message', handleMessage);

  // Setup theme observer for real-time theme sync
  setupThemeObserver();
});

// Watch for app ID change (user clicks different dashboard app in sidebar)
// Must reset iframeSrc so the appUrl watcher rebuilds it with the new app's URL
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && oldId && newId !== oldId) {
      // Clear hash from previous app — it belongs to the old app's route
      if (window.location.hash) {
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        );
      }
      // Reset so the appUrl watcher below picks up the new URL
      iframeSrc.value = '';
    }
  }
);

// Watch for appUrl to be available, then set iframeSrc with hash
watch(
  appUrl,
  newUrl => {
    if (newUrl && !iframeSrc.value) {
      iframeSrc.value = buildIframeSrc();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);

  if (themeObserver) {
    themeObserver.disconnect();
  }

  // Clean up hash only when actually leaving dashboard apps entirely.
  // When switching between dashboard apps, Vue Router will navigate to
  // a new dashboard_apps/:id route — the new component will set its own hash.
  // We use nextTick-equivalent setTimeout(0) to check the destination route.
  setTimeout(() => {
    const currentPath = window.location.pathname;
    // If we navigated away from dashboard_apps, clean the hash
    if (!currentPath.includes('/dashboard_apps/')) {
      if (window.location.hash) {
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        );
      }
    }
  }, 0);
});

const onIframeLoad = () => {
  if (isTypebot.value) {
    fillTypebotEmail();
  }

  // Send initial context after a small delay to ensure iframe is ready
  setTimeout(() => {
    sendContextToIframe();
  }, 500);
};
</script>

<template>
  <div class="h-full w-full flex-1 overflow-hidden">
    <iframe
      v-if="iframeSrc"
      ref="iframeRef"
      :src="iframeSrc"
      class="h-full w-full border-0"
      :title="dashboardApp?.title"
      allowfullscreen
      allow="
        fullscreen;
        autoplay;
        encrypted-media;
        picture-in-picture;
        camera;
        microphone;
        clipboard-read;
        clipboard-write;
        display-capture;
        web-share;
      "
      @load="onIframeLoad"
    />
    <div
      v-else
      class="h-full w-full flex items-center justify-center text-slate-500"
    >
      {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LOADING') }}
    </div>
  </div>
</template>
